/**
 * Ground Collision System
 * 
 * Provides ground detection, slope calculations, and terrain collision
 * for entities moving on the terrain surface.
 * 
 * Features:
 * - Ground height detection via raycasting
 * - Slope angle calculations
 * - Support for terrain mesh and custom ground objects
 * - Collision layer filtering
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Ray } from "@babylonjs/core/Culling/ray";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { IDisposable, IUpdatable } from "../core/Engine.types";
import { GroundCheckResult, CollisionGroup } from "./Physics.types";

/**
 * Ground collision configuration
 */
export interface GroundCollisionConfig {
    /** Maximum raycast distance for ground detection */
    maxRayDistance: number;
    /** Ground detection ray offset (starts above entity) */
    rayOffset: number;
    /** Maximum walkable slope angle in degrees */
    maxSlopeAngle: number;
    /** Ground layer mask for filtering */
    groundLayerMask: number;
    /** Enable debug visualization */
    debugEnabled: boolean;
}

/**
 * Ground mesh registration info
 */
interface GroundMeshInfo {
    mesh: AbstractMesh | Mesh;
    hasPhysics: boolean;
    physicsAggregate?: PhysicsAggregate;
    isStatic: boolean;
}

/**
 * Default ground collision configuration
 */
const DEFAULT_CONFIG: GroundCollisionConfig = {
    maxRayDistance: 100,
    rayOffset: 2,
    maxSlopeAngle: 45,
    groundLayerMask: CollisionGroup.TERRAIN | CollisionGroup.STATIC,
    debugEnabled: false,
};

/**
 * GroundCollision - Ground detection and terrain collision system
 */
export class GroundCollision implements IDisposable, IUpdatable {
    // Scene reference kept for potential future raycasting operations
    private _scene: Scene;
    private _config: GroundCollisionConfig;
    private _groundMeshes: Map<string, GroundMeshInfo> = new Map();
    private _terrainMesh: AbstractMesh | null = null;
    private _debugRays: Map<string, any> = new Map();

    /**
     * Get the scene this system is attached to
     */
    public get scene(): Scene {
        return this._scene;
    }

    /**
     * Get the registered terrain mesh
     */
    public get terrainMesh(): AbstractMesh | null {
        return this._terrainMesh;
    }

    /**
     * Get all registered ground meshes
     */
    public get groundMeshes(): AbstractMesh[] {
        return Array.from(this._groundMeshes.values()).map(info => info.mesh);
    }

    constructor(scene: Scene, config?: Partial<GroundCollisionConfig>) {
        this._scene = scene;
        this._config = { ...DEFAULT_CONFIG, ...config };
    }

    /**
     * Register the main terrain mesh for ground collision
     */
    public setTerrainMesh(mesh: AbstractMesh | Mesh, physicsAggregate?: PhysicsAggregate): void {
        this._terrainMesh = mesh;
        this._groundMeshes.set("terrain", {
            mesh,
            hasPhysics: !!physicsAggregate,
            physicsAggregate,
            isStatic: true,
        });
        console.log(`GroundCollision: Terrain mesh registered "${mesh.name}"`);
    }

    /**
     * Register an additional ground mesh (ramps, platforms, etc.)
     */
    public registerGroundMesh(
        id: string,
        mesh: AbstractMesh | Mesh,
        physicsAggregate?: PhysicsAggregate
    ): void {
        this._groundMeshes.set(id, {
            mesh,
            hasPhysics: !!physicsAggregate,
            physicsAggregate,
            isStatic: true,
        });
        console.log(`GroundCollision: Ground mesh registered "${id}" (${mesh.name})`);
    }

    /**
     * Unregister a ground mesh
     */
    public unregisterGroundMesh(id: string): void {
        this._groundMeshes.delete(id);
    }

    /**
     * Check ground at a world position
     * Returns ground height, normal, slope, and hit information
     */
    public checkGround(position: Vector3, checkDistance?: number): GroundCheckResult {
        const maxDist = checkDistance ?? this._config.maxRayDistance;
        const rayStart = new Vector3(
            position.x,
            position.y + this._config.rayOffset,
            position.z
        );
        const rayDirection = Vector3.Down();
        const ray = new Ray(rayStart, rayDirection, maxDist + this._config.rayOffset);

        // Default result
        const result: GroundCheckResult = {
            isGrounded: false,
            groundHeight: 0,
            normal: Vector3.Up(),
            distance: maxDist,
            slopeAngle: 0,
        };

        // Check against all registered ground meshes
        let closestHit: { distance: number; point: Vector3; normal: Vector3; mesh: AbstractMesh } | null = null;

        for (const info of this._groundMeshes.values()) {
            const hit = ray.intersectsMesh(info.mesh, false);
            if (hit.hit && hit.pickedPoint) {
                const distance = Vector3.Distance(rayStart, hit.pickedPoint);
                if (!closestHit || distance < closestHit.distance) {
                    closestHit = {
                        distance,
                        point: hit.pickedPoint,
                        normal: hit.getNormal(true) ?? Vector3.Up(),
                        mesh: info.mesh,
                    };
                }
            }
        }

        if (closestHit) {
            result.isGrounded = true;
            result.groundHeight = closestHit.point.y;
            result.normal = closestHit.normal;
            result.distance = closestHit.distance - this._config.rayOffset;
            result.groundMesh = closestHit.mesh;

            // Calculate slope angle (angle between normal and up vector)
            const dotProduct = Vector3.Dot(closestHit.normal, Vector3.Up());
            result.slopeAngle = Math.acos(Math.min(1, Math.max(-1, dotProduct)));
        }

        return result;
    }

    /**
     * Get height at XZ position
     * Convenience method for simple height queries
     */
    public getHeightAt(x: number, z: number): number {
        const result = this.checkGround(new Vector3(x, 1000, z), 2000);
        return result.isGrounded ? result.groundHeight : 0;
    }

    /**
     * Check if slope is walkable at position
     */
    public isSlopeWalkable(position: Vector3): boolean {
        const result = this.checkGround(position);
        if (!result.isGrounded) return false;
        
        const maxSlopeRad = (this._config.maxSlopeAngle * Math.PI) / 180;
        return result.slopeAngle <= maxSlopeRad;
    }

    /**
     * Get slope direction at position (direction downhill)
     */
    public getSlopeDirection(position: Vector3): Vector3 {
        const result = this.checkGround(position);
        if (!result.isGrounded || result.slopeAngle < 0.01) {
            return Vector3.Zero();
        }

        // Project normal onto XZ plane and normalize
        const slopeDir = new Vector3(result.normal.x, 0, result.normal.z);
        if (slopeDir.lengthSquared() < 0.001) {
            return Vector3.Zero();
        }
        return slopeDir.normalize();
    }

    /**
     * Snap entity to ground
     * Returns the adjusted Y position
     */
    public snapToGround(position: Vector3, entityHeight: number = 0): number {
        const result = this.checkGround(position);
        if (result.isGrounded) {
            return result.groundHeight + entityHeight;
        }
        return position.y;
    }

    /**
     * Check if position is over ground (has ground below within max distance)
     */
    public isOverGround(position: Vector3, maxDistance?: number): boolean {
        const result = this.checkGround(position, maxDistance);
        return result.isGrounded;
    }

    /**
     * Get nearest ground point from a position
     */
    public getNearestGroundPoint(position: Vector3): Vector3 | null {
        const result = this.checkGround(position);
        if (result.isGrounded) {
            return new Vector3(position.x, result.groundHeight, position.z);
        }
        return null;
    }

    /**
     * Multi-point ground check (for large entities)
     * Checks multiple points and returns average/lowest
     */
    public checkGroundMultiPoint(
        center: Vector3,
        radius: number,
        pointCount: number = 4
    ): { averageHeight: number; lowestHeight: number; highestHeight: number; allGrounded: boolean } {
        const results: GroundCheckResult[] = [];
        
        // Center point
        results.push(this.checkGround(center));
        
        // Points around the perimeter
        for (let i = 0; i < pointCount; i++) {
            const angle = (i / pointCount) * Math.PI * 2;
            const offset = new Vector3(
                Math.cos(angle) * radius,
                0,
                Math.sin(angle) * radius
            );
            results.push(this.checkGround(center.add(offset)));
        }
        
        const groundedResults = results.filter(r => r.isGrounded);
        
        if (groundedResults.length === 0) {
            return {
                averageHeight: 0,
                lowestHeight: 0,
                highestHeight: 0,
                allGrounded: false,
            };
        }
        
        const heights = groundedResults.map(r => r.groundHeight);
        const sum = heights.reduce((a, b) => a + b, 0);
        
        return {
            averageHeight: sum / heights.length,
            lowestHeight: Math.min(...heights),
            highestHeight: Math.max(...heights),
            allGrounded: groundedResults.length === results.length,
        };
    }

    /**
     * Set debug visualization
     */
    public setDebugEnabled(enabled: boolean): void {
        this._config.debugEnabled = enabled;
        if (!enabled) {
            this._clearDebugRays();
        }
    }

    /**
     * Update (for debug visualization)
     */
    public update(_deltaTime: number): void {
        // Debug visualization updated elsewhere if needed
    }

    /**
     * Clear debug visualization
     */
    private _clearDebugRays(): void {
        for (const ray of this._debugRays.values()) {
            if (ray.dispose) ray.dispose();
        }
        this._debugRays.clear();
    }

    /**
     * Set max slope angle
     */
    public setMaxSlopeAngle(degrees: number): void {
        this._config.maxSlopeAngle = degrees;
    }

    /**
     * Dispose resources
     */
    public dispose(): void {
        this._clearDebugRays();
        this._groundMeshes.clear();
        this._terrainMesh = null;
    }
}

export type { GroundCheckResult };
